import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

export const HEADER_FONT = { bold: true, name: 'Arial', size: 10, color: { argb: 'FF000000' } };
export const CENTERED = { horizontal: 'center', vertical: 'middle' };

// Plain or comma-grouped decimals with an optional trailing "%"; leading zeros (e.g. "00123" codes) are left as text.
const NUMERIC_TEXT = /^-?(0|[1-9]\d{0,2}(,\d{3})+|[1-9]\d*)(\.\d+)?%?$/;

/**
 * Converts numeric strings coming from the API into real numbers so Excel doesn't flag
 * them as "Number stored as Text" ("15.6%" becomes 0.156). Values with more than 15
 * significant digits stay text to avoid precision loss on long IDs.
 */
export const toCellValue = value => {
    if (typeof value !== 'string') return value;
    const text = value.trim();
    if (!NUMERIC_TEXT.test(text) || text.replace(/\D/g, '').replace(/^0+/, '').length > 15) return value;
    const number = Number(text.replace(/[,%]/g, ''));
    return text.endsWith('%') ? Number((number / 100).toPrecision(15)) : number;
};

// Keeps the on-screen look (thousands separators, percent sign, decimal places) for converted values.
const numberFormatFor = value => {
    const text = typeof value === 'string' ? value.trim() : '';
    const isPercent = text.endsWith('%');
    if (!isPercent && !text.includes(',')) return undefined;
    const decimals = text.replace('%', '').split('.')[1]?.length || 0;
    const base = text.includes(',') ? '#,##0' : '0';
    return `${base}${decimals ? `.${'0'.repeat(decimals)}` : ''}${isPercent ? '%' : ''}`;
};

/** Writes `values` into `row`, converting numeric strings and keeping thousands separators visible. */
export const setRowValues = (row, values) => {
    values.forEach((value, index) => {
        const cell = row.getCell(index + 1);
        cell.value = toCellValue(value);
        const numFmt = typeof cell.value === 'number' ? numberFormatFor(value) : undefined;
        if (numFmt) cell.numFmt = numFmt;
    });
};

export const downloadWorkbook = async (workbook, fileName) => {
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), fileName);
};

/**
 * Exports rows keyed by header text to a single-sheet workbook with a bold, centered header row.
 * `decorate(sheet)` can apply extra styling before the file is written.
 */
export const exportRowsToExcel = async ({ rows, headers, fileName, columnWidths, decorate }) => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Sheet1');

    sheet.columns = headers.map((header, index) => ({ header, key: header, width: columnWidths?.[index] }));
    (rows || []).forEach(row => setRowValues(sheet.addRow([]), headers.map(header => row[header])));

    sheet.getRow(1).eachCell(cell => {
        cell.font = HEADER_FONT;
        cell.alignment = CENTERED;
    });

    decorate?.(sheet);
    await downloadWorkbook(workbook, fileName);
};
